/**
 * Brands: schemas, list search, queries and mutations (GET/POST/PATCH/DELETE /api/brands).
 *
 * Traps handled here (rgs-api-contract):
 * - GET /brands/:id and the PATCH response carry the whole operator row, `ApiSalt` included; the
 *   schema keeps only the operator's id and name, so Zod drops the rest.
 * - PATCH sets an omitted `Name` to NULL and fails without `ExternalBrandId`: both always go.
 * - The list can't filter by operator or status (not in the allow-list).
 * - A missing brand answers 400 "… not found" instead of 404.
 * - Every brand write flushes the whole shared Redis.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { download, http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { flag, jsonColumn } from '@/api/zod-helpers';
import type { FieldMeta } from '@/lib/diff';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';

export const brandSchema = z.object({
  Id: z.coerce.number().int(),
  operatorId: z.coerce
    .number()
    .int()
    .nullish()
    .transform((v) => v ?? null),
  /** The text key configs, players, awards and whitelists use. Unique per operator; never changes. */
  ExternalBrandId: z.string(),
  Name: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  /** 1 enabled, 2 disabled; barely matters at runtime (rgs-domain). */
  Status: flag,
  /** Detail only: a JSON column of extra settings. */
  additionalData: jsonColumn(z.unknown())
    .optional()
    .transform((v) => v ?? null),
  /** Only the operator's id and name are kept. */
  Operators: z
    .object({ Id: z.coerce.number().int(), Name: z.string() })
    .nullish()
    .transform((v) => v ?? null),
});

export type Brand = z.infer<typeof brandSchema>;

/** GET /brands/:id answers a missing brand with 400 "Brands with ID … not found". */
export const isBrandNotFound = (error: unknown) =>
  isApiError(error) &&
  (error.code === 'not_found' || (error.code === 'validation' && error.messages.some((m) => /not found/i.test(m))));

// ---- list -------------------------------------------------------------------------------------

/** The server's sortable allow-list (module-fields.config.ts, `brands`). */
export const BRAND_SORT_FIELDS = ['Id', 'Name', 'ExternalBrandId'] as const;

export const DEFAULT_BRAND_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

/** The server ANDs filters, so one quick search looks in one field at a time. */
export const BRAND_SEARCH_FIELDS = ['name', 'external'] as const;

export const brandsSearchSchema = listSearchSchema({
  sortFields: BRAND_SORT_FIELDS,
  filters: { searchIn: filterParam.enum(BRAND_SEARCH_FIELDS) },
});

export type BrandsSearch = z.infer<typeof brandsSearchSchema>;

export function brandsPagedQuery(search: BrandsSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) {
    const field = search.searchIn === 'external' ? 'ExternalBrandId' : 'Name';
    filters.push({ field, operator: 'contains', value: search.q });
  }
  return toPagedQuery(search, { defaultSort: DEFAULT_BRAND_SORT, filters });
}

const listSchema = pagedSchema(brandSchema);

export const brandsKeys = {
  all: ['brands'] as const,
  list: (query: PagedQuery) => ['brands', 'list', query] as const,
  detail: (id: number) => ['brands', 'detail', id] as const,
  operatorOptions: ['brands', 'operator-options'] as const,
};

export const brandsListQuery = (search: BrandsSearch) => {
  const query = brandsPagedQuery(search);
  return queryOptions({
    queryKey: brandsKeys.list(query),
    queryFn: ({ signal }) => http('/brands', { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
  });
};

export const brandQuery = (id: number) =>
  queryOptions({
    queryKey: brandsKeys.detail(id),
    queryFn: ({ signal }) => http(`/brands/${id}`, { signal }, brandSchema),
  });

/** Operators to pick from when creating a brand (id and name only; the salt is dropped). */
const operatorOptionSchema = z.object({ Id: z.coerce.number().int(), Name: z.string() });

export const brandOperatorOptionsQuery = () =>
  queryOptions({
    queryKey: brandsKeys.operatorOptions,
    queryFn: async ({ signal }) => {
      const page = await http(
        '/operators',
        { query: encodePagedQuery({ skip: 0, take: 500, sort: { field: 'Name', dir: 'asc' } }), signal },
        pagedSchema(operatorOptionSchema),
      );
      return page.data;
    },
    staleTime: 5 * 60 * 1000,
  });

/** The brands workbook (xlsx) for the current sort and filters. */
export async function exportBrands(search: BrandsSearch) {
  const { sort, filters } = brandsPagedQuery(search);
  const { blob, filename } = await download('/brands/export', {
    query: {
      sort: sort ? JSON.stringify(sort) : undefined,
      filter: filters?.length ? JSON.stringify(filters) : undefined,
    },
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? `brands-${new Date().toISOString().slice(0, 10)}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

// ---- writes -----------------------------------------------------------------------------------

export interface BrandValues extends Record<string, unknown> {
  Name: string;
  ExternalBrandId: string;
  Status: boolean;
}

export const BRAND_FIELDS: FieldMeta[] = [
  { key: 'Name', label: 'Name' },
  { key: 'ExternalBrandId', label: 'External brand ID' },
  { key: 'Status', label: 'Status', format: (v) => (v ? 'Active' : 'Inactive') },
];

/** PATCH trap: Name and ExternalBrandId always travel together. */
export const BRAND_PATCH_ALWAYS = ['Name', 'ExternalBrandId'];

export const toBrandValues = (b: Brand): BrandValues => ({
  Name: b.Name,
  ExternalBrandId: b.ExternalBrandId,
  Status: b.Status,
});

/** Status 1 enabled, 2 disabled. */
function toWireBody(values: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    body[key] = key === 'Status' ? (value ? 1 : 2) : value;
  }
  return body;
}

export function useUpdateBrand(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      http(`/brands/${id}`, { method: 'PATCH', body: toWireBody(patch) }, brandSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: brandsKeys.all });
      // An operator page lists its brands from its own query.
      await queryClient.invalidateQueries({ queryKey: ['operators', 'brands'] });
    },
  });
}

export interface NewBrand extends BrandValues {
  operatorId: number;
}

export function useCreateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: NewBrand) => http('/brands', { method: 'POST', body: toWireBody(values) }, brandSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: brandsKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['operators', 'brands'] });
    },
  });
}

export function useDeleteBrand(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/brands/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: brandsKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: brandsKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['operators', 'brands'] });
    },
  });
}
